/**
 * Yjs Integration Test Page
 * Validates Yjs service, IndexedDB persistence, and WebSocket sync
 * 
 * How to test:
 * 1. Open this page in 2+ browser tabs
 * 2. Add/edit/delete records - should sync across tabs
 * 3. Go offline (DevTools Network → Offline) - edits save locally
 * 4. Go back online - changes auto-sync
 * 5. Close and reopen tab - data persists
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Container,
    Stack,
    Divider,
    Alert
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useYjsRecords, SyncStatus } from '../../services/yjs';
import { SyncStatusBadge, PendingChangesIndicator } from '../Yjs';

export default function YjsTestPage() {
    const [newRecordName, setNewRecordName] = useState('');
    
    // Configuration for multi-user testing
    const [roomName, setRoomName] = useState('yjs-test-room');
    const [userId, setUserId] = useState('1');
    const [isConfigOpen, setIsConfigOpen] = useState(false);

    // Custom hook wrapping Yjs state, IndexedDB offline persistence, and WebSocket sync
    const {
        records,
        addRecord,
        deleteRecord,
        isHydrated,
        isConnected,
        isSynced,
        pendingChanges,
        lastSyncTime
    } = useYjsRecords(roomName, {
        roomName,
        uid: parseInt(userId)
    });

    const syncStatus = !navigator.onLine
        ? SyncStatus.OFFLINE
        : isSynced
            ? SyncStatus.SYNCED
            : isConnected
                ? SyncStatus.CONNECTED
                : SyncStatus.CONNECTING;

    // Add record
    const handleAddRecord = () => {
        if (!newRecordName.trim()) return;

        addRecord({
            id: Date.now(),
            name: newRecordName.trim(),
            value: Math.floor(Math.random() * 100),
            timestamp: new Date().toISOString()
        });

        setNewRecordName('');
        console.log('[TEST] Added record via useYjsRecords:', newRecordName);
    };

    // Delete record
    const handleDeleteRecord = (index) => {
        deleteRecord(index);
        console.log('[TEST] Deleted record via useYjsRecords at index:', index);
    };

    // Force refresh (testing)
    const handleRefresh = () => {
        console.log('[TEST] Current synchronized records:', records.length);
    };

    if (!isHydrated) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Typography>Loading Yjs (Hydrating from IndexedDB)...</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Stack spacing={3}>
                    {/* Header */}
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            Yjs Integration Test
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Open multiple tabs to test real-time sync. Go offline to test persistence.
                        </Typography>
                    </Box>

                    {/* Status */}
                    <Box>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <SyncStatusBadge 
                                status={syncStatus}
                                pendingChanges={pendingChanges}
                                lastSyncTime={lastSyncTime}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {records.length} records
                            </Typography>
                        </Stack>
                    </Box>

                    {/* Pending changes warning */}
                    <PendingChangesIndicator 
                        pendingChanges={pendingChanges}
                        changeCount={records.length}
                    />

                    <Divider />

                    {/* Add record form */}
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Add Record
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Record Name"
                                value={newRecordName}
                                onChange={(e) => setNewRecordName(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') handleAddRecord();
                                }}
                            />
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleAddRecord}
                                disabled={!newRecordName.trim()}
                            >
                                Add
                            </Button>
                            <IconButton onClick={handleRefresh} title="Refresh">
                                <RefreshIcon />
                            </IconButton>
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Records list */}
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Records ({records.length})
                        </Typography>
                        {records.length === 0 ? (
                            <Typography color="text.secondary" variant="body2">
                                No records yet. Add one to get started!
                            </Typography>
                        ) : (
                            <List>
                                {records.map((record, index) => (
                                    <ListItem
                                        key={record.id || index}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                onClick={() => handleDeleteRecord(index)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText
                                            primary={record.name}
                                            secondary={`Value: ${record.value} | ID: ${record.id}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>

                    {/* Debug info */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="caption" component="pre" sx={{ m: 0 }}>
                            {JSON.stringify({
                                status: syncStatus,
                                pendingChanges,
                                recordCount: records.length,
                                lastSync: lastSyncTime ? new Date(lastSyncTime).toLocaleTimeString() : 'Never'
                            }, null, 2)}
                        </Typography>
                    </Box>
                </Stack>
            </Paper>
        </Container>
    );
}
